import React, { useState, UIEvent } from 'react'

import Modal from 'client/components/Modal'
import { PhotoCounter } from 'src/comps/Molecules/PhotosCarousel/styles'
import PhotosOverview from 'src/comps/PhotosOverview'
import PhotosCategories from 'src/comps/Molecules/PhotosCategories'
import useGtmEvent from 'src/hooks/useGtmEvent'
import { ImageApiObject } from 'src/types/image'
import { useDevice } from 'src/hooks/useDevice'

import { CATEGORIES } from './utils'
import { DEVICE_PHOTOS_INFO } from 'src/comps/Gallery/utils'
import { Content, ComposedPhotosCarousel } from './styles'

const CATEGORIES_HEIGHT = 60

interface Props {
  focusOnImageIndex: number
  photos: ImageApiObject[]
  CTA?: JSX.Element | false
}

const PhotosGallery = ({ focusOnImageIndex, photos, CTA }: Props) => {
  const { device } = useDevice()

  const [showModal, setShowModal] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(focusOnImageIndex)
  const [focusOnZoomedImageIndex, setFocusOnZoomedImageIndex] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [filteredPhotos, setFilteredPhotos] = useState(photos)
  const [showCta, setShowCta] = useState(device !== 'mobile')

  const { notify: notifyClick } = useGtmEvent({ eventName: 'click_to_zoom' })
  const { notify: notifyZoom } = useGtmEvent({ eventName: 'scroll_img_modal', onlyOnce: true })

  const categories = CATEGORIES.map(category => ({
    ...category,
    count:
      category.id === 'all'
        ? photos.length
        : photos.filter(photo => photo.code === category.id).length,
  }))

  const onPhotoClick = (index: number) => {
    notifyClick()
    setFocusOnZoomedImageIndex(index)
    setCurrentSlide(index)
    setShowModal(true)
  }

  const onCategoryClick = (category: string) => {
    const photosForCategory =
      category === 'all'
        ? photos
        : photos
            .filter(photo => photo.code === category)
            .sort((a, b) => (a.categoryOrder ?? 0) - (b.categoryOrder ?? 0))

    setSelectedCategory(category)
    setFilteredPhotos(photosForCategory)
  }

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    if (event.currentTarget.scrollTop > DEVICE_PHOTOS_INFO[device].avgPhotoHeight * 2) {
      notifyZoom()
    }
    if (event.currentTarget.scrollTop > CATEGORIES_HEIGHT && device === 'mobile') {
      setShowCta(true)
    }
    if (event.currentTarget.scrollTop < CATEGORIES_HEIGHT && device === 'mobile') {
      setShowCta(false)
    }
  }

  return (
    <>
      <Content onScroll={onScroll} showCta={showCta} data-testid="gallery-content">
        <PhotosCategories
          categories={categories}
          onCategoryClick={onCategoryClick}
          selectedCategory={selectedCategory}
        />
        <PhotosOverview
          photos={filteredPhotos}
          focusOnImageIndex={focusOnImageIndex}
          onPhotoClick={onPhotoClick}
        />
      </Content>
      {showCta && CTA}
      <Modal
        title={<PhotoCounter>{`${currentSlide + 1} / ${filteredPhotos.length}`}</PhotoCounter>}
        show={showModal}
        variant="fullscreen"
        handleClose={() => setShowModal(false)}
        unfreezeScroll={false}
      >
        <ComposedPhotosCarousel
          photos={filteredPhotos}
          focusOnImageIndex={focusOnZoomedImageIndex}
          onChange={setCurrentSlide}
        />
        {CTA}
      </Modal>
    </>
  )
}

export default PhotosGallery
